// Public list view: Classes / Workshops / Series / Courses as cards,
// with a day strip and Teacher / Location filters.
import { useState, useEffect, useMemo } from "react";
import {
  DAYS, formatTime, toLocalDateStr, isEventPast, isWorkshopPast, parseLocalDate,
  workshopDateRange, resolveImg, findTeacher, KIND_LABELS, workshopKind, spaceInfo, spaceLabel,
} from "./calendarUtils";
import "./AgendaView.css";

const HORIZON_DAYS = 56;
const TABS = [
  { id: "classes",  label: "Classes" },
  { id: "workshop", label: "Workshops" },
  { id: "series",   label: "Series" },
  { id: "course",   label: "Courses" },
];

// ─── Formatting helpers ───────────────────────────────────
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addMinutes(time, mins) {
  const [h, m] = time.split(":").map(Number);
  const t = h * 60 + m + (mins || 0);
  return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}
const timeRange = (time, dur) => `${formatTime(time)} – ${formatTime(addMinutes(time, dur))}`;
const longDate  = d => d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const shortDate = d => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const monthDay  = d => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const money     = n => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

function dayHeading(dateStr, todayStr) {
  if (dateStr === todayStr) return "Today";
  if (dateStr === toLocalDateStr(addDays(parseLocalDate(todayStr), 1))) return "Tomorrow";
  return longDate(parseLocalDate(dateStr));
}

// ─── Icons ────────────────────────────────────────────────
const Icon = ({ d }) => (
  <svg className="ag-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const ICONS = {
  date:   <Icon d={<><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></>} />,
  time:   <Icon d={<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>} />,
  place:  <Icon d={<><path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.3" /></>} />,
  online: <Icon d={<><circle cx="12" cy="12" r="1.6" /><path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.5 5.5a9.2 9.2 0 0 0 0 13M18.5 5.5a9.2 9.2 0 0 1 0 13" /></>} />,
  list:   <Icon d={<><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></>} />,
};

// ─── Card pieces ──────────────────────────────────────────
function Description({ text }) {
  const [open, setOpen] = useState(false);
  if (!text?.trim()) return null;
  const long = text.length > 220 || text.split("\n").length > 3;
  return (
    <div className="ag-desc">
      <p className={`ag-desc-text ${long && !open ? "ag-desc-text--clamped" : ""}`}>{text}</p>
      {long && <button type="button" className="ag-link" onClick={() => setOpen(o => !o)}>{open ? "Show less" : "Show more"}</button>}
    </div>
  );
}

function TeacherLine({ API, name, teacher }) {
  const [showBio, setShowBio] = useState(false);
  if (!name) return null;
  const photo = resolveImg(API, teacher?.image);
  return (
    <div className="ag-teacher">
      <div className="ag-teacher-row">
        {photo
          ? <img className="ag-avatar" src={photo} alt="" loading="lazy" />
          : <span className="ag-avatar ag-avatar--initial" aria-hidden="true">{name[0]}</span>}
        <span className="ag-teacher-name">{name}</span>
        {teacher?.bio && <>
          <span className="ag-sep" aria-hidden="true">•</span>
          <button type="button" className="ag-link" onClick={() => setShowBio(b => !b)}>{showBio ? "Hide bio" : "Show bio"}</button>
        </>}
      </div>
      {showBio && <p className="ag-bio">{teacher.bio}</p>}
    </div>
  );
}

function Card({ API, kind, kindLabel, image, fallbackImage, title, teacherName, teacher, description,
                meta, price, priceNote, cancelled, onBook, space = null }) {
  const img = resolveImg(API, image) || resolveImg(API, fallbackImage);
  return (
    <article className={`ag-card ${cancelled ? "ag-card--cancelled" : ""}`}>
      <div className={`ag-thumb ag-thumb--${kind}`}>
        {img ? <img src={img} alt="" loading="lazy" /> : <span className="ag-thumb-initial" aria-hidden="true">{(title || "?")[0]}</span>}
      </div>
      <div className="ag-body">
        <span className={`ag-kind ag-kind--${kind}`}>{kindLabel}</span>
        <h3 className="ag-title">{title}</h3>
        <TeacherLine API={API} name={teacherName} teacher={teacher} />
        <Description text={description} />
        <ul className="ag-meta">
          {meta.filter(Boolean).map((m, i) => <li key={i}>{ICONS[m.icon]}<span>{m.text}</span></li>)}
        </ul>
      </div>
      <div className="ag-side">
        <div className="ag-price-block">
          {price != null && <span className="ag-price">{price}</span>}
          {priceNote && <span className="ag-price-note">{priceNote}</span>}
          {space && !cancelled && (
            <span className={`ag-space${space.full ? " ag-space--full" : ""}`}>{spaceLabel(space)}</span>
          )}
        </div>
        {cancelled
          ? <span className="ag-cancelled">Cancelled</span>
          : space?.full && !space.waitlist
            ? <button type="button" className="ag-book" disabled>Full</button>
            : <button type="button" className="ag-book" onClick={onBook}>{space?.full ? "Join waitlist" : "Book now"}</button>}
      </div>
    </article>
  );
}

// ─── Main view ────────────────────────────────────────────
export function AgendaView({ API, today, classes, workshops, cancelledDates, dateOverrides, venues, taken = {},
                             onBookClass, onBookWorkshop }) {
  const todayStr = toLocalDateStr(today);
  const [teachers, setTeachers]   = useState([]);
  const [tab, setTab]             = useState("classes");
  const [stripStart, setStripStart] = useState(() => startOfDay(today));
  const [day, setDay]             = useState(null);   // "YYYY-MM-DD" or null = all upcoming
  const [teacherF, setTeacherF]   = useState("");
  const [venueF, setVenueF]       = useState("");

  useEffect(() => {
    fetch(`${API}/teachers`)
      .then(r => (r.ok ? r.json() : { teachers: [] }))
      .then(d => setTeachers(d.teachers ?? []))
      .catch(() => setTeachers([]));
  }, [API]);

  const venueName = id => id === "venue_online" ? "Online"
    : (venues.find(v => v.id === id)?.name || (id && !/^venue_/.test(id) ? id.split(",")[0].trim() : ""));

  // Every upcoming class occurrence in the horizon (overrides applied, past ones dropped).
  const occurrences = useMemo(() => {
    const out   = [];
    const start = parseLocalDate(todayStr);
    for (let i = 0; i < HORIZON_DAYS; i++) {
      const date    = addDays(start, i);
      const dateStr = toLocalDateStr(date);
      const dow     = date.getDay();
      classes
        .filter(c => !c.isPrivate && c.dayOfWeek === dow
          && (!c.startDate || dateStr >= c.startDate) && (!c.endDate || dateStr <= c.endDate))
        .forEach(c => {
          const key = `${c._id}__${dateStr}`;
          const evt = { ...c, ...(dateOverrides.get(key) || {}) };
          if (isEventPast(dateStr, evt.time, evt.duration)) return;
          out.push({ date, dateStr, evt, cancelled: cancelledDates.has(key) });
        });
    }
    return out.sort((a, b) => a.dateStr.localeCompare(b.dateStr) || a.evt.time.localeCompare(b.evt.time));
  }, [classes, cancelledDates, dateOverrides, todayStr]);

  const upcomingWs = useMemo(() => workshops
    .filter(w => w.days?.length && !isWorkshopPast(w))
    .map(w => ({ w, kind: workshopKind(w), dates: workshopDateRange(w) }))
    .sort((a, b) => a.dates[0].localeCompare(b.dates[0])), [workshops]);

  // Filter options come from what's actually on the schedule.
  const teacherOptions = useMemo(() => [...new Set([
    ...occurrences.map(o => o.evt.teacher), ...upcomingWs.map(x => x.w.teacher),
  ].map(t => (t || "").trim()).filter(Boolean))].sort(), [occurrences, upcomingWs]);

  const venueOptions = useMemo(() => {
    const ids = new Set([...occurrences.map(o => o.evt.venue), ...upcomingWs.flatMap(x => x.w.days.map(d => d.venue))]);
    return [...ids].filter(Boolean).map(id => ({ id, name: venueName(id) })).filter(v => v.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrences, upcomingWs, venues]);

  const matchTeacher = name => !teacherF || (name || "").trim() === teacherF;
  const classesF  = occurrences.filter(o => matchTeacher(o.evt.teacher) && (!venueF || o.evt.venue === venueF));
  const wsByKind  = k => upcomingWs.filter(x => x.kind === k && matchTeacher(x.w.teacher)
                                         && (!venueF || x.w.days.some(d => d.venue === venueF)));

  const counts = { classes: classesF.length, workshop: wsByKind("workshop").length,
                   series: wsByKind("series").length, course: wsByKind("course").length };
  const visibleTabs = TABS.filter(t => t.id === "classes" ? occurrences.length > 0
    : upcomingWs.some(x => x.kind === t.id));
  const activeTab = visibleTabs.some(t => t.id === tab) ? tab : (visibleTabs[0]?.id ?? "classes");

  const datesWithItems = useMemo(() => {
    const set = new Set();
    if (activeTab === "classes") classesF.forEach(o => set.add(o.dateStr));
    else wsByKind(activeTab).forEach(x => x.dates.forEach(d => set.add(d)));
    return set;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, classesF.length, teacherF, venueF, upcomingWs]);

  const stripDays   = Array.from({ length: 7 }, (_, i) => addDays(stripStart, i));
  const atStart     = toLocalDateStr(stripStart) <= todayStr;
  const shiftStrip  = n => {
    const next = addDays(stripStart, n);
    setStripStart(toLocalDateStr(next) < todayStr ? parseLocalDate(todayStr) : next);
  };

  // ── Card builders
  const classCard = ({ date, dateStr, evt, cancelled }) => {
    const teacher = findTeacher(teachers, evt.teacher);
    const sliding = evt.priceMin != null && evt.priceMax != null;
    const place   = evt.venue === "venue_online" ? null : venueName(evt.venue);
    return (
      <Card key={`${evt._id}__${dateStr}`} API={API} kind="class"
        kindLabel={evt.subject ? `Class · ${evt.subject}` : "Class"}
        image={evt.image} fallbackImage={teacher?.image}
        title={evt.title} teacherName={evt.teacher} teacher={teacher} description={evt.description}
        meta={[
          { icon: "date", text: longDate(date) },
          { icon: "time", text: `${timeRange(evt.time, evt.duration)} · ${evt.duration} min` },
          place && { icon: "place", text: place },
          (evt.venue === "venue_online" || evt.isHybrid) && { icon: "online", text: evt.isHybrid ? "In studio or online" : "Online" },
        ]}
        price={sliding ? `${money(evt.priceMin)}–${money(evt.priceMax)}` : money(evt.price)}
        priceNote="Drop-in · or use a pass"
        cancelled={cancelled}
        space={spaceInfo(evt, taken[`${evt.id}__${dateStr}`])}
        onBook={() => onBookClass(evt, date)} />
    );
  };

  const workshopCard = ({ w, kind, dates }) => {
    const teacher   = findTeacher(teachers, w.teacher);
    const first     = parseLocalDate(dates[0]);
    const last      = parseLocalDate(dates[dates.length - 1]);
    const sessions  = w.days.flatMap(d => d.sessions.map(s => ({ ...s, date: d.date })));
    const s0        = w.days[0]?.sessions[0];
    const places    = [...new Set(w.days.map(d => venueName(d.venue)).filter(n => n && n !== "Online"))];
    const online    = w.days.some(d => d.venue === "venue_online");
    const single    = kind === "workshop" || w.allowSingle !== false;
    const perPrices = sessions.map(s => Number(s.price) || 0).filter(n => n > 0);
    const meta = [];
    if (kind === "series") {
      meta.push({ icon: "date", text: `${shortDate(first)} – ${shortDate(last)}` });
      if (s0) meta.push({ icon: "time", text: `${DAYS[first.getDay()]} · ${timeRange(s0.time, s0.duration)} · ${s0.duration} min` });
      meta.push({ icon: "list", text: `${sessions.length} sessions` });
    } else if (kind === "course") {
      meta.push({ icon: "date", text: `Ends ${longDate(last)}` });
      meta.push({ icon: "list", text: `${sessions.length} sessions: ${dates.map(d => monthDay(parseLocalDate(d))).join(" · ")}` });
      if (s0) meta.push({ icon: "time", text: `${timeRange(s0.time, s0.duration)} · ${s0.duration} min` });
    } else {
      meta.push({ icon: "date", text: dates.length === 1 ? longDate(first) : `${shortDate(first)} – ${shortDate(last)} · ${dates.length} days` });
      if (dates.length === 1 && sessions.length) {
        const lastS = sessions[sessions.length - 1];
        meta.push({ icon: "time", text: `${formatTime(sessions[0].time)} – ${formatTime(addMinutes(lastS.time, lastS.duration))}` });
      }
      if (sessions.length > 1) meta.push({ icon: "list", text: `${sessions.length} sessions` });
    }
    if (places.length) meta.push({ icon: "place", text: places.join(" · ") });
    if (online) meta.push({ icon: "online", text: "Online" });

    return (
      <Card key={w.id} API={API} kind={kind} kindLabel={KIND_LABELS[kind]}
        image={w.image} fallbackImage={teacher?.image}
        title={w.title} teacherName={w.teacher} teacher={teacher} description={w.description}
        meta={meta}
        price={w.pricing?.fullWorkshop ? money(w.pricing.fullWorkshop) : null}
        priceNote={single && perPrices.length ? `or ${money(Math.min(...perPrices))} per session` : null}
        onBook={() => onBookWorkshop(w)} />
    );
  };

  // ── Content for the active tab
  let content;
  if (activeTab === "classes") {
    const list = day ? classesF.filter(o => o.dateStr === day) : classesF;
    let lastDate = null;
    content = list.length === 0 ? null : list.map(o => {
      const heading = o.dateStr !== lastDate ? <h4 className="ag-day-heading" key={`h-${o.dateStr}`}>{dayHeading(o.dateStr, todayStr)}</h4> : null;
      lastDate = o.dateStr;
      return [heading, classCard(o)];
    });
  } else {
    const list = wsByKind(activeTab).filter(x => !day || x.dates.includes(day));
    content = list.length === 0 ? null : list.map(workshopCard);
  }

  return (
    <div className="ag">
      <nav className="ag-tabs" aria-label="Schedule type">
        {visibleTabs.map(t => (
          <button key={t.id} type="button"
            className={`ag-tab ${activeTab === t.id ? "is-active" : ""}`}
            onClick={() => { setTab(t.id); }}>
            {t.label}
            {counts[t.id] > 0 && activeTab !== t.id && <span className="ag-tab-count">{counts[t.id]}</span>}
          </button>
        ))}
      </nav>

      <div className="ag-strip">
        <button type="button" className="ag-strip-nav" onClick={() => shiftStrip(-7)} disabled={atStart} aria-label="Previous week">‹</button>
        {stripDays.map(d => {
          const ds = toLocalDateStr(d);
          return (
            <button key={ds} type="button"
              className={`ag-strip-day ${day === ds ? "is-selected" : ""} ${ds === todayStr ? "is-today" : ""}`}
              onClick={() => setDay(day === ds ? null : ds)}>
              <span className="ag-strip-dow">{DAYS[d.getDay()]}</span>
              <span className="ag-strip-num">{d.getDate()}</span>
              <span className={`ag-strip-dot ${datesWithItems.has(ds) ? "has-items" : ""}`} aria-hidden="true" />
            </button>
          );
        })}
        <button type="button" className="ag-strip-nav" onClick={() => shiftStrip(7)} aria-label="Next week">›</button>
      </div>

      <div className="ag-filters">
        <button type="button" className={`ag-chip ${!day ? "is-active" : ""}`} onClick={() => setDay(null)}>Show all</button>
        <button type="button" className={`ag-chip ${day === todayStr ? "is-active" : ""}`}
          onClick={() => { setStripStart(parseLocalDate(todayStr)); setDay(todayStr); }}>Today</button>
        {teacherOptions.length > 1 && (
          <select className="ag-select" value={teacherF} onChange={e => setTeacherF(e.target.value)} aria-label="Teacher">
            <option value="">All teachers</option>
            {teacherOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {venueOptions.length > 1 && (
          <select className="ag-select" value={venueF} onChange={e => setVenueF(e.target.value)} aria-label="Location">
            <option value="">All locations</option>
            {venueOptions.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
      </div>

      <div className="ag-list">
        {content ?? (
          <p className="ag-empty">
            Nothing scheduled{day ? ` on ${longDate(parseLocalDate(day))}` : ""}{teacherF ? ` with ${teacherF}` : ""}.
          </p>
        )}
      </div>
    </div>
  );
}
